import React, { useState, useContext, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Context as DataSourceContext } from '../../DataSourceContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleUp,
  faAngleDown,
  faTrashAlt,
  faEdit,
} from '@fortawesome/free-solid-svg-icons';
import { MapEditorRouteContext } from '../../../CustomRoutes/MapEditor';
import TinyIconButton from '../../../Shared/TinyIconButton';
import AppContext from '../../../../contexts/AppContext';
import positionableApi from '../../../../utils/api/positionable';
import { MAP_VERSION_DATE_LOCKED_MESSAGE } from '../../mapVersionLock';

const LegendCardContent = ({
  options,
  feature,
  handleBackgroundClick,
  close,
  readOnly = false,
}) => {
  const dataSourceContext = useContext(DataSourceContext);
  const { mapMobileView, setMapMobileView, legendDatesLocked } =
    useContext(MapEditorRouteContext);
  const isTabletOrLess = useMediaQuery({ maxWidth: 768 });
  const [showMethods, setShowMethods] = useState(true);
  const [isEditModeForAO, setIsEditModeForAO] = useState(false);
  const [isEditModeForRemovedOn, setIsEditModeForRemovedOn] = useState(false);
  const [updatedAddedOnDate, setUpdatedAddedOnDate] = useState('');
  const appContext = useContext(AppContext);
  const isAdmin = appContext.state.isAdmin;
  const otherAllowedRoles = [
    'Area / Assistant Manager',
    'Client Inspector',
    'Client Manager',
    'Divisions Admin',
    'Inspector',
    'Regional Manager',
    'Regional Manager (External)',
    'SWMP Admin',
  ];
  const isAllowed = otherAllowedRoles.includes(appContext.state.user.roleName);

  /**
   * SW2-2541 — the Added On / Removed On pickers decide which versions show this item, so they are
   * locked with add and remove (on every version) for anyone the map's lock applies to.
   */
  const canEditDates = (isAdmin || isAllowed) && !legendDatesLocked;
  const lockedDateIcon = legendDatesLocked ? (
    <span title={MAP_VERSION_DATE_LOCKED_MESSAGE} style={{ marginLeft: 6, color: '#888' }}>
      <i className='fa fa-lock' aria-hidden='true' />
    </span>
  ) : (
    <></>
  );
  const [authorName, setAuthorName] = useState('');
  const [deletedDate, setDeletedDate] = useState();

  let inputDate;

  const { positionable_type, positionable_source_id, positionables_id } =
    feature || {};

  //get all available Pollutants and Control measures in one object
  const filteredDataSource = dataSourceContext.get();

  //for Legend Name
  const regexForName = new RegExp(/^pollutant$|^control_measure$/);
  const relevantKeysForName = Object.keys(filteredDataSource).filter((ele) =>
    regexForName.test(ele),
  );
  const allAvailableLegendsForName =
    relevantKeysForName.length &&
    filteredDataSource &&
    filteredDataSource[`${relevantKeysForName[0]}`].map((ele) => ele);

  relevantKeysForName.length &&
    relevantKeysForName[1] &&
    allAvailableLegendsForName.push(
      ...filteredDataSource[`${relevantKeysForName[1]}`].map((ele) => ele),
    );

  //for Legend
  let allAvailableLegends = [],
    legendObject = [];

  let addedOnDate, createdDate, displayDate;

  const regex = new RegExp(/^pollutant--.*|^control_measure--.*/);
  const relevantKeys = Object.keys(filteredDataSource).filter((ele) =>
    regex.test(ele),
  );

  if (relevantKeys.length) {
    relevantKeys.forEach((key) => {
      if (Array.isArray(filteredDataSource[`${key}`])) {
        allAvailableLegends.push(
          ...filteredDataSource[`${key}`].map((ele) => ele),
        );
      }
    });
  }

  if (allAvailableLegends.length) {
    legendObject = allAvailableLegends.filter(
      (ele) => ele.id === positionables_id,
    );
    addedOnDate =
      legendObject[0] &&
      new Date(legendObject[0].createdAt).toLocaleString('en-US').split(',')[0];
    createdDate =
      legendObject[0] && legendObject[0].created_date
        ? new Date(legendObject[0].created_date)
            .toLocaleString('en-US')
            .split(',')[0]
        : null;

    displayDate = addedOnDate ? (createdDate ? createdDate : addedOnDate) : ``;

    if (positionables_id in dataSourceContext.tempAddedOnDates) {
      displayDate = dataSourceContext.tempAddedOnDates[positionables_id];
    }
  }

  /**
   * Resolve a footer action by its `action` key rather than by position.
   *
   * These options come from `getProjectUploadOptions`, which gained an
   * "Add Finding" entry at the front of the array (SW2-2230). Reading
   * `options[0]` / `options[1]` therefore silently moved this footer one slot
   * over: Edit opened the Add Finding modal and Remove entered shape/location
   * edit mode. Nothing else on the map addresses these by index — the desktop
   * right-click menu renders them by label — so the mis-wiring only ever showed
   * up on the mobile card.
   */
  const findOption = (action) =>
    (options || []).find((option) => option && option.action === action);

  const editOption = findOption('modify');
  const deleteOption = findOption('delete');

  /** Mirrors the disabled treatment in Shared/ContextMenu. */
  const actionProps = (option) => ({
    onClick: option.isDisabled ? undefined : () => option.onClick(),
    title: option.isDisabled ? option.disabledMessage || undefined : undefined,
    style: option.isDisabled ? { cursor: 'not-allowed', opacity: 0.7 } : undefined,
  });

  const legendName = () => {
    const legendObjectForName = allAvailableLegendsForName.filter(
      (ele) => ele.id === positionable_source_id,
    );

    return legendObjectForName.length ? `${legendObjectForName[0].name}` : null;
  };

  const legendNumber = () => {
    return legendObject.length ? `${legendObject[0].number}` : null;
  };

  const onAddedOnChangeHandler = (event) => {
    const date = new Date(event.target.value);
    const offset = date.getTimezoneOffset() * 60000;

    inputDate = new Date(date.getTime() + offset)
      .toLocaleString('en-US')
      .split(',')[0];
  };

  const editAddedOnHandler = () => {
    if (inputDate && legendObject.length) {
      positionableApi
        .editCreatedDate(legendObject[0].id, inputDate)
        .then((res) => {
          dataSourceContext.setTempAddedOnDates({
            ...dataSourceContext.tempAddedOnDates,
            [positionables_id]: inputDate,
          });
          setUpdatedAddedOnDate(inputDate);
        })
        .catch((error) => console.log(error));
    }
  };

  const addedOnEditMode = () => {
    return (
      <>
        <input
          type='date'
          min={
            isAllowed
              ? new Date(
                  new Date(addedOnDate).getTime() - 7 * 24 * 60 * 60 * 1000,
                )
                  .toISOString()
                  .split('T')[0]
              : null
          }
          max={new Date(addedOnDate).toISOString().split('T')[0]}
          onChange={(event) => onAddedOnChangeHandler(event)}
          placeholder='Select Date'
          onKeyDown={(event) => event.preventDefault()}
        />
        <TinyIconButton
          iconName='check'
          title='Click to save the added on date'
          onClick={() => {
            editAddedOnHandler();
            setIsEditModeForAO(false);
          }}
        />
      </>
    );
  };

  const addedOnDisplayMode = () => {
    return (
      <>
        {`${updatedAddedOnDate}`}
        {canEditDates ? (
          <TinyIconButton
            iconName='pencil'
            title='Click to edit the added on date'
            onClick={() => {
              setIsEditModeForAO(true);
            }}
          />
        ) : (
          lockedDateIcon
        )}
      </>
    );
  };

  const removedOnHandler = () => {
    if (inputDate && legendObject.length) {
      positionableApi
        .editRemovedOnDate(legendObject[0].id, inputDate)
        .then((res) => {
          dataSourceContext.setTempRemovedOnDates({
            ...dataSourceContext.tempRemovedOnDates,
            [positionables_id]: inputDate,
          });
          setDeletedDate(inputDate);
        })
        .catch((error) => console.log(error));
    }
  };

  const removedOnEditMode = () => {
    return (
      <>
        <input
          type='date'
          min={
            new Date(new Date(deletedDate).getTime() - 7 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0]
          }
          max={new Date(deletedDate).toISOString().split('T')[0]}
          onChange={(event) => onAddedOnChangeHandler(event)}
          placeholder='Select Date'
          onKeyDown={(event) => event.preventDefault()}
        />
        <TinyIconButton
          iconName='check'
          title='Click to save the added on date'
          onClick={() => {
            removedOnHandler();
            setIsEditModeForRemovedOn(false);
          }}
        />
      </>
    );
  };

  const removedOnDisplayMode = () => {
    const displayDate =
      positionables_id &&
      dataSourceContext.tempRemovedOnDates &&
      dataSourceContext.tempRemovedOnDates[positionables_id]
        ? dataSourceContext.tempRemovedOnDates[positionables_id]
        : deletedDate;

    return (
      <>
        {`${displayDate}`}
        {canEditDates ? (
          <TinyIconButton
            iconName='pencil'
            title='Click to edit the added on date'
            onClick={() => {
              setIsEditModeForRemovedOn(true);
            }}
          />
        ) : (
          lockedDateIcon
        )}
      </>
    );
  };

  if (isTabletOrLess) {
    setMapMobileView(true);
  }

  useEffect(() => {
    setUpdatedAddedOnDate(displayDate);
  }, [displayDate]);

  const fetchAddedByName = () => {
    if (positionables_id) {
      positionableApi
        .getPositionablesAuthor(positionables_id)
        .then((res) => {
          setAuthorName(res.data.author_initials);
        })
        .catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    fetchAddedByName();
  }, [positionables_id]);

  useEffect(() => {
    const removedDate =
      legendObject[0] && legendObject[0].deleted_date
        ? new Date(legendObject[0].deleted_date)
            .toLocaleString('en-US')
            .split(',')[0]
        : null;

    setDeletedDate(removedDate);
  }, [allAvailableLegends]);

  return (
    <>
      <div className='LegendCardContent'>
        <header>
          <span>
            {legendName()}-{legendNumber()}
          </span>
          <span onClick={(event) => handleBackgroundClick(event, close)}>
            CLOSE
          </span>
        </header>

        <div className='LegendCardContent__added'>
          {allAvailableLegends.length ? (
            <p className='LegendCardContent__added-left'>
              <span>{`Added On: `}</span>
              <span>
                {isEditModeForAO && canEditDates ? addedOnEditMode() : addedOnDisplayMode()}
              </span>
            </p>
          ) : (
            <></>
          )}

          <p className='LegendCardContent__added-right'>
            {`Added By: `}
            <span>{`${authorName}`}</span>
          </p>
        </div>

        <div className='LegendCardContent__added'>
          {allAvailableLegends.length && deletedDate ? (
            <p className='LegendCardContent__added-left'>
              <span>{`Removed On: `}</span>
              <span>
                {isEditModeForRemovedOn && canEditDates
                  ? removedOnEditMode()
                  : removedOnDisplayMode()}
              </span>
            </p>
          ) : (
            <></>
          )}
        </div>

        <div className='LegendCardContent__center'>
          <p>{`(${positionable_type})`}</p>
          {!readOnly ? (
            <p>
              <FontAwesomeIcon
                className='modal-icon'
                icon={showMethods ? faAngleUp : faAngleDown}
                style={{
                  visibility:
                    mapMobileView || isTabletOrLess ? 'visible' : 'hidden',
                }}
                onClick={() => setShowMethods(!showMethods)}
              />
            </p>
          ) : (
            <></>
          )}
        </div>

        {!readOnly && (deleteOption || editOption) ? (
          <footer
            style={{
              display: !showMethods || !mapMobileView ? 'none' : 'flex',
            }}
          >
            {deleteOption ? (
              <p {...actionProps(deleteOption)}>
                <FontAwesomeIcon icon={faTrashAlt} color='rgba(250,0,0,0.6)' />{' '}
                Remove
              </p>
            ) : (
              <></>
            )}
            {editOption ? (
              <p {...actionProps(editOption)}>
                <FontAwesomeIcon icon={faEdit} /> Edit
              </p>
            ) : (
              <></>
            )}
          </footer>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default LegendCardContent;
